import { api } from '@packages/backend/convex/_generated/api';
import { type ArticleCvx, getUserErrorMessage } from '@packages/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ErrorAlert } from '@/components/ErrorAlert';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ActionButton } from '@/components/ui+/ActionButton';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { ArticleForm } from './components/ArticleForm';

export const Route = createFileRoute('/a/articles/$articleId')({
  component: Component,
});

/** `$articleId` is the literal `new` to create an article, otherwise an article id to edit. */
function Component() {
  const { articleId } = Route.useParams();
  const navigate = useNavigate();
  const goToList = () => void navigate({ to: '/a/articles' });

  if (articleId === 'new') {
    return <ArticleForm key="new" onCancel={goToList} onSaved={goToList} />;
  }

  return <EditExistingArticle articleId={articleId} onDone={goToList} />;
}

function EditExistingArticle({ articleId, onDone }: { articleId: string; onDone: () => void }) {
  const convex = useConvex();
  const { data: articles, isPending, isError, error } = useQuery(api.articles.list);
  const [articleToDelete, setArticleToDelete] = useState<ArticleCvx | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!articleToDelete) return;
    try {
      setIsDeleting(true);
      await convex.mutation(api.articles.remove, { articleId: articleToDelete._id });
      toast.success('Article deleted');
      setArticleToDelete(null);
      onDone();
    } catch (err) {
      toast.error('Unable to delete article', { description: getUserErrorMessage(err) });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isError) return <ErrorAlert message={error.message} title="Error loading article" />;
  if (isPending) return <LoadingIcon />;

  const article = articles.find((a) => a._id === articleId);
  if (!article) return <ErrorAlert message="This article no longer exists." title="Not found" />;

  return (
    <>
      <ArticleForm
        article={article}
        key={article._id}
        onCancel={onDone}
        onDelete={() => setArticleToDelete(article)}
        onSaved={onDone}
      />

      <AlertDialog
        open={!!articleToDelete}
        onOpenChange={(open) => !open && setArticleToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{`Delete '${articleToDelete?.title}'?`}</AlertDialogTitle>
            <AlertDialogDescription>
              The article will be removed from the knowledge base and its search index. This can't
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <ActionButton loading={isDeleting} onClick={handleDelete} variant="destructive">
              Permanently delete
            </ActionButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
